import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Phone, Truck, Store, Check, FileDown, CheckSquare, Square, Link2, Copy, CreditCard, CalendarClock, MessageCircle, PhoneCall, Mail, Wallet, ScanLine, AlertTriangle, Banknote } from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { money, STATUS_META, DELIVERY_COLOR, PAYMENT_TYPE_LABELS } from "@/lib/orderStatus";
import { BulkTrackingModal } from "@/components/orders/BulkTrackingModal";

const PAY_META = {
  online: { Icon: CreditCard, cls: "text-[#0077B6]", key: "payOnline" },
  on_delivery: { Icon: Truck, cls: "text-[#E85D04]", key: "payOnDelivery" },
  on_pickup: { Icon: Store, cls: "text-[#7048E8]", key: "payOnPickup" },
};

const TABS = [
  { key: "commandes", label: "Commandes", statuses: ["a_valider", "a_payer"] },
  { key: "preparation", label: "Préparation", statuses: ["preparation"] },
  { key: "livraison", label: "Livraison / Retrait", statuses: ["en_livraison", "remis_transporteur", "en_attente_retrait"] },
  { key: "termine", label: "Terminé", statuses: ["livre", "refuse", "annule", "echec_livraison"] },
  { key: "all", label: "Archivé", statuses: null },
];

// Mention de paiement affichée sur les cartes de la colonne « Commandes » (statut a_valider).
const payMention = (o, tr) => {
  if (o.payment_type === "on_pickup" || o.mode === "retrait")
    return { label: tr("orders.mPickup"), cls: "bg-[#7048E8]/15 text-[#7048E8]" };
  if (o.payment_type === "on_delivery")
    return { label: tr("orders.mCod"), cls: "bg-[#E85D04]/15 text-[#E85D04]" };
  if (o.payment_type === "interac_etransfer")
    return o.paid
      ? { label: tr("orders.mInteracPaid"), cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" }
      : { label: tr("orders.mInteracWait"), cls: "bg-[#E85D04]/15 text-[#E85D04]" };
  if (o.paid)
    return { label: tr("orders.mPaidOnline"), cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]" };
  return { label: tr("status.a_valider"), cls: "bg-[#FFBA08]/20 text-[#B07d00]" };
};

const termineMeta = (o, tr) => {
  if (o.status === "livre") return o.mode === "retrait"
    ? { label: tr("orders.tPickedUp"), cls: "bg-[#7048E8]/15 text-[#7048E8]", dot: "bg-[#7048E8]" }
    : { label: tr("orders.tDelivered"), cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]", dot: "bg-[#2D6A4F]" };
  if (o.status === "refuse") return { label: tr("orders.tRefused"), cls: "bg-[#D00000]/15 text-[#D00000]", dot: "bg-[#D00000]" };
  if (o.status === "annule") return { label: tr("orders.tCancelled"), cls: "bg-[#9c968a]/20 text-[#5C574F]", dot: "bg-[#9c968a]" };
  if (o.status === "echec_livraison") return { label: tr("orders.tNotDelivered"), cls: "bg-[#E4A11B]/20 text-[#B07d00]", dot: "bg-[#E4A11B]" };
  return { ...(STATUS_META[o.status] || { label: o.status, cls: "" }), dot: "bg-[#9c968a]" };
};

export default function Orders() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState("commandes");
  const [selected, setSelected] = useState(null);
  const [sel, setSel] = useState([]);
  const [tour, setTour] = useState(null);
  const [handoffResult, setHandoffResult] = useState(null);
  const [bulkBatch, setBulkBatch] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [driverId, setDriverId] = useState("");
  const [paySel, setPaySel] = useState([]);
  const [payBusy, setPayBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const ocrFileRef = useRef(null);
  const { data: orders = [] } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data, refetchInterval: 15000 });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });
  const { data: business } = useQuery({ queryKey: ["business"], queryFn: async () => (await api.get("/business")).data });

  const tourEnabled = !["CA", "FR"].includes(business?.country);
  const carrierRegion = ["CA", "FR"].includes(business?.country);
  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];
  const filtered = activeTab.statuses ? orders.filter((o) => activeTab.statuses.includes(o.status)) : orders;
  const isPrep = tab === "livraison";
  const isPayValidation = tab === "commandes";
  const isTermine = tab === "termine";
  // Panier tournée : uniquement les LIVRAISONS prêtes (jamais les retraits)
  const canSelectForTour = (o) => tourEnabled && isPrep && o.mode === "livraison" && o.status === "en_livraison" && !o.in_tour;
  // Panier remise au transporteur (CA/FR) : livraisons prêtes, regroupées par transporteur identique
  const carrierHandoff = carrierRegion && isPrep;
  // Transporteur réel choisi par le client : FlagShip (o.carrier) sinon transporteur/zone manuel (delivery_quartier)
  const orderCarrier = (o) => ((o?.carrier || o?.delivery_quartier) || "").trim();
  const selectedCarrier = sel.length ? orderCarrier(orders.find((o) => o.order_id === sel[0])) : null;
  const canSelectForCarrier = (o) => carrierHandoff && o.mode === "livraison" && o.status === "en_livraison"
    && (sel.length === 0 || sel.includes(o.order_id) || orderCarrier(o) === selectedCarrier);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const togglePay = (id) => setPaySel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const generateTour = async () => {
    if (!driverId) { toast.error(tr("orders.selectDriverErr")); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/delivery/tours", { order_ids: sel, driver_id: driverId });
      setTour(data); setSel([]); setAssignOpen(false); setDriverId("");
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handoffCarrier = async () => {
    if (!sel.length) return;
    setCreating(true);
    try {
      const carrier = orderCarrier(orders.find((o) => o.order_id === sel[0]));
      const { data } = await api.post("/orders/handoff-carrier", { order_ids: sel, carrier });
      toast.success(tr("orders.handoffDone", { count: data.count, carrier: carrier || tr("orders.noCarrier") }));
      const handed = sel
        .map((id) => orders.find((o) => o.order_id === id))
        .filter(Boolean)
        .map((o) => ({ order_id: o.order_id, customer_name: o.customer_name, tracking_number: o.tracking_number || "", shipping_source: o.shipping_source || "", flagship_shipment_id: o.flagship_shipment_id || "" }));
      setHandoffResult({ ...data, carrier, orders: handed });
      setSel([]);
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || tr("orders.handoffFailed"));
    } finally {
      setCreating(false);
    }
  };

  const [batchLabels, setBatchLabels] = useState(false);
  const generateBatchLabels = async () => {
    const ids = (handoffResult?.orders || []).filter((o) => o.shipping_source === "flagship" && !o.flagship_shipment_id).map((o) => o.order_id);
    if (!ids.length) return;
    if (!window.confirm(tr("orders.flagshipBulkConfirm", { count: ids.length }))) return;
    setBatchLabels(true);
    try {
      const { data } = await api.post("/orders/flagship-label-bulk", { order_ids: ids });
      if (data.created > 0) toast.success(tr("orders.flagshipBulkDone", { created: data.created }));
      if (data.failed > 0) toast.error(tr("orders.flagshipBulkFailed", { failed: data.failed }));
      qc.invalidateQueries({ queryKey: ["orders"] });
      setHandoffResult(null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBatchLabels(false);
    }
  };

  const runOcrReconcile = async (e) => {
    const file = e.target.files?.[0];
    if (ocrFileRef.current) ocrFileRef.current.value = "";
    if (!file) return;
    setOcrBusy(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/orders/reconcile-ocr", { image_base64: b64 });
      const ids = (data.matches || []).map((m) => m.order_id);
      if (ids.length > 0) {
        setPaySel((s) => Array.from(new Set([...s, ...ids])));
        toast.success(tr("orders.ocrMatched", { count: ids.length }));
      } else if ((data.transactions || []).length === 0) {
        toast.error(tr("orders.ocrNoTransactions"));
      } else {
        toast.warning(tr("orders.ocrNoMatch"));
      }
      setOcrResult(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || tr("orders.ocrFailed"));
    } finally {
      setOcrBusy(false);
    }
  };

  const confirmPayments = async (idsArg) => {
    const idList = (idsArg && idsArg.length ? idsArg : paySel);
    if (!idList.length) return;
    setPayBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of idList) {
      try {
        await api.put(`/orders/${id}/confirm-payment`, {});
        ok++;
      } catch (err) {
        failed++;
        console.error(`Échec confirmation paiement ${id}:`, err.response?.data?.detail || err);
      }
    }
    if (ok > 0) toast.success(tr("orders.paysConfirmed", { count: ok }));
    if (failed > 0) toast.error(tr("orders.paysFailed", { count: failed }));
    setPaySel((s) => s.filter((id) => !idList.includes(id)));
    setPayBusy(false);
    setOcrResult(null);
    qc.invalidateQueries({ queryKey: ["orders"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
  };

  return (
    <div className="px-4 pt-6">
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-1">{tr("nav.orders")}</h1>
      <p className="text-sm text-[#5C574F] mb-4">{tr("orders.sub")}</p>

      <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible no-scrollbar pb-2 mb-3 scroll-smooth" style={{ WebkitOverflowScrolling: "touch" }}>
        {TABS.map((t) => {
          const count = t.statuses ? orders.filter((o) => t.statuses.includes(o.status)).length : orders.length;
          return (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => { setTab(t.key); setSel([]); setPaySel([]); }}
              className={`shrink-0 md:shrink h-9 px-4 md:px-3.5 rounded-full text-sm md:text-[13px] font-semibold whitespace-nowrap transition-colors ${tab === t.key ? "bg-[#1B4332] text-white" : "bg-white text-[#5C574F] border border-[#E5E0D8]"}`}
            >
              {tr(`orderTabs.${t.key}`)}{count > 0 ? ` (${count})` : ""}
            </button>
          );
        })}
      </div>

      {isPayValidation && filtered.some((o) => o.status === "a_payer") && (
        <div className="mb-3" data-testid="pay-validation-hint">
          <div className="bg-[#0077B6]/8 border border-[#0077B6]/20 rounded-xl p-3 text-xs text-[#0077B6]">
            <Wallet className="h-4 w-4 inline mr-1" /> {tr("orders.payHint")}
          </div>
          <input ref={ocrFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={runOcrReconcile} data-testid="ocr-file-input" />
          <button
            data-testid="ocr-reconcile-btn"
            onClick={() => ocrFileRef.current?.click()}
            disabled={ocrBusy}
            className="w-full h-12 mt-2 rounded-2xl bg-[#1B4332] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          >
            <ScanLine className="h-5 w-5" /> {ocrBusy ? tr("orders.ocrAnalyzing") : tr("orders.ocrBtn")}
          </button>
          <p className="text-[11px] text-[#9c968a] mt-1.5">{tr("orders.ocrHint")}</p>
        </div>
      )}

      {carrierHandoff && filtered.some((o) => o.status === "en_livraison" && o.mode === "livraison") && (
        <div className="mb-3 bg-[#0077B6]/8 border border-[#0077B6]/20 rounded-xl p-3 text-xs text-[#0077B6]" data-testid="carrier-handoff-hint">
          <Truck className="h-4 w-4 inline mr-1" /> {tr("orders.carrierBatchHint")}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[#9c968a]">
          <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{tr("orders.noOrders")}</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((o, i) => {
          const meta = isTermine ? termineMeta(o, tr) : STATUS_META[o.status];
          const statusLabel = isTermine ? meta.label : tr(`status.${o.status}`);
          const dotCls = isTermine ? meta.dot : (DELIVERY_COLOR[o.delivery_color] || DELIVERY_COLOR.green).dot;
          const selForTour = canSelectForTour(o);
          const selForPay = isPayValidation && o.status === "a_payer";
          const selForCarrier = canSelectForCarrier(o);
          return (
            <motion.div
              key={o.order_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              data-testid={`order-${o.order_id}`}
              className="w-full text-left bg-white rounded-2xl border border-black/5 shadow-sm p-4"
            >
              <button onClick={() => setSelected(o)} className="w-full text-left">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold flex items-center gap-2">
                    {(selForTour || selForPay || selForCarrier) && (
                      <span
                        role="checkbox"
                        aria-checked={selForPay ? paySel.includes(o.order_id) : sel.includes(o.order_id)}
                        data-testid={`select-order-${o.order_id}`}
                        onClick={(e) => { e.stopPropagation(); selForPay ? togglePay(o.order_id) : toggleSel(o.order_id); }}
                        className="text-[#1B4332]"
                      >
                        {(selForPay ? paySel : sel).includes(o.order_id) ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-[#cfc8ba]" />}
                      </span>
                    )}
                    <span className={`h-2.5 w-2.5 rounded-full ${dotCls}`} />
                    {o.customer_name}
                    {o.in_tour && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0077B6]/15 text-[#0077B6] font-semibold" data-testid={`in-tour-${o.order_id}`}>{tr("orders.inTour")}</span>}
                  </span>
                  {o.status === "a_valider"
                    ? (() => { const pm = payMention(o, tr); return <span className={`text-xs px-3 py-1 rounded-full font-semibold ${pm.cls}`} data-testid={`pay-mention-${o.order_id}`}>{pm.label}</span>; })()
                    : <span className={`text-xs px-3 py-1 rounded-full font-semibold ${meta.cls}`}>{statusLabel}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs text-[#9c968a] mb-2">
                  {isPrep ? (() => {
                    const isPickup = o.mode === "retrait" || o.payment_type === "on_pickup";
                    const isCod = !isPickup && o.payment_type === "on_delivery";
                    const label = isPickup ? tr("orders.prepPickup") : isCod ? tr("orders.prepCod") : tr("orders.prepPaidOnline");
                    return (
                      <>
                        {isPickup ? <Store className="h-3.5 w-3.5" /> : <Truck className="h-3.5 w-3.5" />}
                        <span className="font-medium text-[#5C574F]" data-testid={`mode-tag-${o.order_id}`}>{label}</span>
                      </>
                    );
                  })() : (
                    <>
                      {o.mode === "livraison" ? <Truck className="h-3.5 w-3.5" /> : <Store className="h-3.5 w-3.5" />}
                      <span>{o.mode === "livraison" ? tr("orders.delivery") : tr("orders.pickup")}</span>
                    </>
                  )}
                  <span>•</span>
                  <span>{tr("orders.items", { count: o.items.reduce((s, it) => s + it.qty, 0) })}</span>
                </div>
                {isPrep && carrierRegion && o.mode === "livraison" && (
                  <div className="flex items-center gap-1.5 text-xs mb-2 rounded-lg bg-[#0077B6]/8 border border-[#0077B6]/20 px-2 py-1.5" data-testid={`carrier-chip-${o.order_id}`}>
                    <Truck className="h-3.5 w-3.5 text-[#0077B6] shrink-0" />
                    <span className="text-[#5C574F]">{tr("orders.chosenCarrier")}</span>
                    <span className="font-bold text-[#0077B6] truncate">{orderCarrier(o) || tr("orders.noCarrier")}</span>
                    {o.shipping_source === "flagship" && <span className="ml-auto text-[9px] font-bold uppercase tracking-wide text-[#1B4332] bg-[#1B4332]/10 px-1.5 py-0.5 rounded shrink-0">FlagShip</span>}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[#9c968a]">#{o.order_id.slice(-6)}</span>
                  <span className="font-display font-bold text-[#E85D04]">{money(o.total, o.currency)}</span>
                </div>
              </button>

              {isPayValidation && o.payment_reference && (
                <div className="mt-2 bg-[#0077B6]/8 rounded-lg px-2.5 py-1.5" data-testid={`pay-ref-card-${o.order_id}`}>
                  <p className="text-xs text-[#0077B6]"><span className="font-semibold">{tr("orders.refClient")}</span> {o.payment_reference}</p>
                </div>
              )}

              {o.status === "echec_livraison" && o.delivery_failed_reason && (
                <div className="mt-2 bg-[#D00000]/8 border border-[#D00000]/20 rounded-lg px-2.5 py-1.5" data-testid={`card-fail-reason-${o.order_id}`}>
                  <p className="text-xs text-[#D00000]"><span className="font-semibold">{tr("orders.failReason")}</span> {o.delivery_failed_reason}</p>
                </div>
              )}

              <div className="mt-2 pt-2 border-t border-[#F0ece4] flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0">
                  <p className="text-[#9c968a] truncate" data-testid={`invoice-no-${o.order_id}`}>{tr("orders.invoiceNo")} <span className="font-semibold text-[#5C574F]">{o.invoice_number || "—"}</span></p>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`view-pdf-${o.order_id}`}
                  onClick={(e) => { e.stopPropagation(); window.open(`${API}/public/orders/${o.order_id}/invoice.pdf`, "_blank"); }}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F9F6F0] text-[#5C574F] font-semibold hover:text-[#E85D04] transition-colors"
                >
                  <FileDown className="h-3.5 w-3.5" /> {tr("orders.viewPdf")}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AnimatePresence>
        {selected && (
          <OrderModal
            order={selected}
            onClose={() => setSelected(null)}
            onUpdated={(updated) => { qc.invalidateQueries({ queryKey: ["orders"] }); qc.invalidateQueries({ queryKey: ["stats"] }); setSelected(updated); }}
          />
        )}
      </AnimatePresence>

      {/* Barre flottante : panier de validation des paiements */}
      {isPayValidation && paySel.length > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md" data-testid="pay-confirm-bar">
          <button
            onClick={confirmPayments}
            disabled={payBusy}
            data-testid="confirm-payments-btn"
            className="w-full h-14 rounded-2xl bg-[#2D6A4F] text-white font-bold shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          >
            <Wallet className="h-5 w-5" /> {payBusy ? tr("orders.confirming") : tr("orders.confirmPaysBtn", { count: paySel.length })}
          </button>
        </div>
      )}

      {/* Barre flottante : préparer la tournée (Afrique) */}
      {isPrep && tourEnabled && sel.length > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md" data-testid="tour-bar">
          <button
            onClick={() => setAssignOpen(true)}
            data-testid="generate-tour-btn"
            className="w-full h-14 rounded-2xl bg-[#0077B6] text-white font-bold shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-transform"
          >
            <Link2 className="h-5 w-5" /> {tr("orders.prepareTour", { count: sel.length })}
          </button>
        </div>
      )}

      {/* Barre flottante : remise au transporteur (Canada / France) */}
      {carrierHandoff && sel.length > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md" data-testid="handoff-bar">
          <button
            onClick={handoffCarrier}
            disabled={creating}
            data-testid="handoff-carrier-btn"
            className="w-full h-14 rounded-2xl bg-[#0077B6] text-white font-bold shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          >
            <Truck className="h-5 w-5" /> {creating ? tr("orders.confirming") : tr("orders.handoffBar", { carrier: selectedCarrier || tr("orders.noCarrier"), count: sel.length })}
          </button>
        </div>
      )}

      <AnimatePresence>
        {assignOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setAssignOpen(false)} data-testid="assign-driver-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
              <div className="flex items-center gap-2 mb-1"><Truck className="h-6 w-6 text-[#0077B6]" /><h3 className="font-display text-xl font-extrabold">{tr("orders.assignDriver")}</h3></div>
              <p className="text-sm text-[#5C574F] mb-4">{tr("orders.assignSub", { count: sel.length })}</p>
              {drivers.length === 0 ? (
                <div className="text-center bg-[#F9F6F0] rounded-xl p-5 mb-4">
                  <p className="text-sm text-[#5C574F] mb-3">{tr("orders.noDriver")}</p>
                  <button data-testid="goto-drivers-btn" onClick={() => navigate("/livreurs")} className="h-11 px-4 rounded-xl bg-[#1B4332] text-white font-semibold text-sm">{tr("orders.addDriver")}</button>
                </div>
              ) : (
                <select data-testid="driver-select" value={driverId} onChange={(e) => setDriverId(e.target.value)} className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#0077B6] mb-4">
                  <option value="">{tr("orders.selectDriver")}</option>
                  {drivers.map((d) => <option key={d.driver_id} value={d.driver_id}>{d.first_name} {d.last_name} {d.transport ? `— ${d.transport}` : ""} {d.zone ? `(${d.zone})` : ""}</option>)}
                </select>
              )}
              <div className="flex gap-2">
                <button data-testid="assign-cancel-btn" onClick={() => setAssignOpen(false)} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">{tr("common.cancel")}</button>
                <button data-testid="confirm-tour-btn" onClick={generateTour} disabled={creating || !driverId} className="flex-1 h-12 rounded-2xl bg-[#0077B6] text-white font-bold disabled:opacity-50">{creating ? tr("orders.generating") : tr("orders.generateTour")}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {tour && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setTour(null)} data-testid="tour-link-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
              <div className="flex items-center gap-2 mb-1"><Truck className="h-6 w-6 text-[#0077B6]" /><h3 className="font-display text-xl font-extrabold">{tr("orders.tourSent")}</h3></div>
              <p className="text-sm text-[#5C574F] mb-4">{tr("orders.tourSentSub", { count: tour.count, name: tour.driver_name, phone: tour.driver_phone })}</p>
              <div className="bg-[#F9F6F0] rounded-xl p-3 text-xs break-all text-[#5C574F] mb-3" data-testid="tour-link-text">{tour.link}</div>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="copy-tour-link" onClick={() => { navigator.clipboard?.writeText(tour.link); toast.success(tr("orders.linkCopied")); }} className="h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold flex items-center justify-center gap-1.5"><Copy className="h-4 w-4" /> {tr("common.copy")}</button>
                <a data-testid="whatsapp-tour-link" href={`https://wa.me/${(tour.driver_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(tr("orders.waTourMsg", { link: tour.link }))}`} target="_blank" rel="noreferrer" className="h-12 rounded-2xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-1.5"><Phone className="h-4 w-4" /> WhatsApp</a>
              </div>
              <button onClick={() => setTour(null)} className="w-full h-11 mt-3 rounded-2xl text-[#5C574F] font-semibold">{tr("common.close")}</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {handoffResult && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setHandoffResult(null)} data-testid="handoff-result-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6">
              <div className="flex items-center gap-2 mb-1"><Truck className="h-6 w-6 text-[#0077B6]" /><h3 className="font-display text-xl font-extrabold">{tr("orders.handedToCarrier")} ✅</h3></div>
              <p className="text-sm text-[#5C574F] mb-4">{tr("orders.handoffDone", { count: handoffResult.count, carrier: handoffResult.carrier || tr("orders.noCarrier") })}</p>
              <a
                data-testid="download-manifest-btn"
                href={`${API}/orders/carrier-batch/${handoffResult.batch_id}/manifest.pdf`}
                target="_blank"
                rel="noreferrer"
                className="w-full h-12 rounded-2xl bg-[#0077B6] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <FileDown className="h-5 w-5" /> {tr("orders.downloadManifest")}
              </a>
              {(handoffResult.orders || []).some((o) => o.shipping_source === "flagship" && !o.flagship_shipment_id) && (
                <button
                  data-testid="generate-batch-labels-btn"
                  onClick={generateBatchLabels}
                  disabled={batchLabels}
                  className="w-full h-12 mt-2 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
                >
                  <FileDown className="h-5 w-5" /> {batchLabels ? tr("common.sending") : tr("orders.flagshipBulkGenerate")}
                </button>
              )}
              {(handoffResult.orders || []).length > 0 && (
                <button
                  data-testid="open-bulk-tracking-btn"
                  onClick={() => { setBulkBatch({ carrier: handoffResult.carrier, orders: handoffResult.orders }); setHandoffResult(null); }}
                  className="w-full h-12 mt-2 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                  <Truck className="h-5 w-5" /> {tr("orders.bulkTracking")}
                </button>
              )}
              <p className="text-xs text-[#9c968a] mt-2 text-center">{tr("orders.bulkTrackingLater")}</p>
              <button onClick={() => setHandoffResult(null)} className="w-full h-11 mt-3 rounded-2xl text-[#5C574F] font-semibold">{tr("common.close")}</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bulkBatch && (
          <BulkTrackingModal
            batch={bulkBatch}
            onClose={() => setBulkBatch(null)}
            onSaved={() => qc.invalidateQueries({ queryKey: ["orders"] })}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {ocrResult && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setOcrResult(null)} data-testid="ocr-result-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[88vh] overflow-y-auto no-scrollbar">
              {(() => {
                const cur = filtered[0]?.currency || "XOF";
                const txs = ocrResult.transactions || [];
                const matches = ocrResult.matches || [];
                const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
                const matchedRefs = new Set(matches.map((m) => norm(m.reference)).filter(Boolean));
                const matchedAmts = new Set(matches.map((m) => Math.round(Number(m.amount) || 0)).filter(Boolean));
                const unmatched = txs.filter((t) => {
                  const r = norm(t.reference); const a = Math.round(Number(t.amount) || 0);
                  return !((r && matchedRefs.has(r)) || (a && matchedAmts.has(a)));
                });
                return (
                  <>
                    <div className="flex items-center gap-2 mb-4"><ScanLine className="h-6 w-6 text-[#1B4332]" /><h3 className="font-display text-xl font-extrabold">{tr("orders.ocrResultTitle")}</h3></div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      {[["ocr-stat-read", txs.length, tr("orders.ocrStatRead"), "#0077B6"], ["ocr-stat-matched", matches.length, tr("orders.ocrStatMatched"), "#2D6A4F"], ["ocr-stat-unmatched", unmatched.length, tr("orders.ocrStatUnmatched"), "#E4A11B"]].map(([tid, n, lbl, col]) => (
                        <div key={tid} data-testid={tid} className="rounded-xl p-3 text-center" style={{ background: `${col}12` }}>
                          <p className="font-display text-2xl font-extrabold" style={{ color: col }}>{n}</p>
                          <p className="text-[11px] text-[#5C574F] leading-tight mt-0.5">{lbl}</p>
                        </div>
                      ))}
                    </div>
                    {txs.length === 0 && <p className="text-sm text-[#9c968a]">{tr("orders.ocrNoTransactions")}</p>}
                    {unmatched.length > 0 && (
                      <div className="mb-2" data-testid="ocr-unmatched-list">
                        <p className="text-sm font-semibold text-[#B07d00] flex items-center gap-1.5 mb-1.5"><AlertTriangle className="h-4 w-4" /> {tr("orders.ocrUnmatchedTitle")}</p>
                        <div className="space-y-2">
                          {unmatched.map((t, i) => (
                            <div key={`${t.reference || "noref"}-${i}`} className="bg-[#FFBA08]/10 rounded-xl p-3 text-sm" data-testid={`ocr-unmatched-${i}`}>
                              <div className="flex justify-between items-start gap-2">
                                <span className="font-mono text-xs text-[#5C574F] break-all">{t.reference || "—"}</span>
                                <span className="font-bold text-[#1A1A1A] shrink-0">{money(Number(t.amount) || 0, cur)}</span>
                              </div>
                              {t.sender && <p className="text-xs text-[#9c968a] mt-0.5">{tr("orders.ocrSender")}: {t.sender}</p>}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-[#9c968a] mt-2">{tr("orders.ocrUnmatchedHint")}</p>
                      </div>
                    )}
                    <div className="mt-3 space-y-2">
                      {matches.length > 0 && (
                        <button onClick={() => confirmPayments(matches.map((m) => m.order_id))} disabled={payBusy} data-testid="ocr-confirm-direct" className="w-full h-12 rounded-2xl bg-[#2D6A4F] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
                          <Wallet className="h-5 w-5" /> {payBusy ? tr("orders.confirming") : tr("orders.ocrConfirmDirect", { count: matches.length })}
                        </button>
                      )}
                      <button onClick={() => setOcrResult(null)} data-testid="ocr-result-close" className="w-full h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold active:scale-95 transition-transform">{matches.length > 0 ? tr("orders.ocrReview") : tr("common.close")}</button>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OrderModal({ order, onClose, onUpdated }) {
  const { t: tr } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [showRefuse, setShowRefuse] = useState(false);
  const [refuseReason, setRefuseReason] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [reNotify, setReNotify] = useState(true);
  const [reNote, setReNote] = useState("");
  const meta = STATUS_META[order.status] || STATUS_META.annule;
  const currency = order.currency || "XOF";

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/orders/${order.order_id}/validate`, {});
      toast.success(tr("orders.validated"));
      onUpdated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/orders/${order.order_id}/status`, { status });
      toast.success(tr("orders.statusUpdated"));
      onUpdated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/reschedule`, { notify: reNotify, note: reNote.trim() });
      toast.success(reNotify ? tr("orders.rescheduledNotify") : tr("orders.rescheduled"));
      setShowReschedule(false); setReNote("");
      onUpdated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const refuse = async () => {
    const reason = refuseReason.trim();
    if (!reason) { toast.error(tr("orders.reasonRequired")); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/orders/${order.order_id}/refuse`, { reason });
      toast.success(tr("orders.refused"));
      setShowRefuse(false);
      onUpdated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const sharePaymentLink = () => {
    const link = `${window.location.origin}/commande/${order.order_id}`;
    const msg = tr("orders.waValidatedMsg", { name: order.customer_name, total: money(order.total, currency), link });
    window.open(`https://wa.me/${order.customer_phone.replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const [sendingInvoice, setSendingInvoice] = useState(false);
  const resendInvoice = async () => {
    setSendingInvoice(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/send-invoice`);
      toast.success(data.message || tr("orders.invoiceSent"));
      onUpdated({ ...order, invoice_last_sent_at: data.sent_at, invoice_sends: [...(order.invoice_sends || []), data.sent_at] });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSendingInvoice(false);
    }
  };

  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [savingTracking, setSavingTracking] = useState(false);
  const saveTracking = async () => {
    if (!tracking.trim()) return;
    setSavingTracking(true);
    try {
      const { data } = await api.put(`/orders/${order.order_id}/tracking`, { tracking_number: tracking.trim() });
      toast.success(tr("orders.trackingSaved"));
      onUpdated(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingTracking(false);
    }
  };

  const [reminding, setReminding] = useState(false);
  const remindClient = async () => {
    setReminding(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/remind`);
      toast.success(data.message || tr("orders.reminderSent"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setReminding(false);
    }
  };

  const [genLabel, setGenLabel] = useState(false);
  const generateFlagshipLabel = async () => {
    if (!window.confirm(tr("orders.flagshipConfirm"))) return;
    setGenLabel(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/flagship-label`);
      toast.success(tr("orders.flagshipCreated", { tracking: data.tracking_number }));
      onUpdated({ ...order, flagship_shipment_id: data.shipment_id, tracking_number: data.tracking_number, flagship_label_url: "y", tracking_url: data.label_pdf });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setGenLabel(false);
    }
  };
  const cancelFlagshipLabel = async () => {
    if (!window.confirm(tr("orders.flagshipCancelConfirm"))) return;
    setGenLabel(true);
    try {
      await api.delete(`/orders/${order.order_id}/flagship-label`);
      toast.success(tr("orders.flagshipCancelled"));
      onUpdated({ ...order, flagship_shipment_id: "", tracking_number: "", flagship_label_url: "", tracking_url: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setGenLabel(false);
    }
  };

  const [confirmingInterac, setConfirmingInterac] = useState(false);
  const confirmInterac = async () => {
    if (!window.confirm(tr("orders.interacConfirmPrompt"))) return;
    setConfirmingInterac(true);
    try {
      await api.post(`/orders/${order.order_id}/confirm-interac`);
      toast.success(tr("orders.interacConfirmed"));
      onUpdated({ ...order, paid: true, status: "a_valider", payment_method: "interac_etransfer" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setConfirmingInterac(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl font-bold">{order.customer_name}</h3>
            <a href={`tel:${order.customer_phone}`} className="text-sm text-[#0077B6] flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {order.customer_phone}</a>
            {(() => {
              const ch = order.contact_channel || "whatsapp";
              const cmeta = ch === "email"
                ? { Icon: Mail, label: tr("orders.emailCh"), color: "#D44638" }
                : ch === "gsm"
                ? { Icon: PhoneCall, label: tr("orders.gsmCh"), color: "#0077B6" }
                : { Icon: MessageCircle, label: tr("orders.waCh"), color: "#25D366" };
              const { Icon } = cmeta;
              return (
                <div className="mt-1.5 flex flex-col gap-0.5" data-testid="order-contact-channel">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold w-fit px-2 py-0.5 rounded-full" style={{ background: `${cmeta.color}1A`, color: cmeta.color }}>
                    <Icon className="h-3 w-3" /> {tr("orders.contactPref", { channel: cmeta.label })}
                  </span>
                  {ch === "email" && order.customer_email && (
                    <a href={`mailto:${order.customer_email}`} data-testid="order-customer-email" className="text-xs text-[#0077B6] flex items-center gap-1"><Mail className="h-3 w-3" /> {order.customer_email}</a>
                  )}
                </div>
              );
            })()}
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>
        <span className={`inline-block text-xs px-3 py-1 rounded-full font-semibold ${meta.cls} mb-4`}>{tr(`status.${order.status}`)}</span>

        {(order.payment_type || order.slots?.length > 0) && (
          <div className="bg-[#F9F6F0] rounded-xl p-3 mb-4 space-y-1.5" data-testid="order-modalities">
            {order.payment_type && (
              <p className="text-sm flex items-center gap-2 text-[#5C574F]">
                {(() => {
                  const pm = PAY_META[order.payment_type] || PAY_META.online;
                  const PIcon = pm.Icon;
                  return (<><PIcon className={`h-4 w-4 ${pm.cls}`} /><span className="font-semibold">{tr(`orders.${pm.key}`)}</span></>);
                })()}
              </p>
            )}
            {order.slots?.length > 0 && (
              <p className="text-sm flex items-start gap-2 text-[#5C574F]" data-testid="order-slots">
                <CalendarClock className="h-4 w-4 text-[#E85D04] mt-0.5" />
                <span>{order.mode === "retrait" ? tr("orders.slotPickup") : tr("orders.slotsWanted")}<span className="font-semibold">{order.slots.map((s) => `${s.date} (${s.time})`).join(" / ")}</span></span>
              </p>
            )}
            {order.slots?.length > 0 && (order.status === "en_livraison" || order.status === "en_attente_retrait") && (
              <button
                data-testid="remind-client-btn"
                onClick={remindClient}
                disabled={reminding}
                className="w-full h-10 mt-1 rounded-xl bg-white border border-[#E85D04]/40 text-[#E85D04] font-semibold text-sm flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-60"
              >
                <CalendarClock className="h-4 w-4" /> {reminding ? tr("common.sending") : tr("orders.remindWa")}
              </button>
            )}
          </div>
        )}

        {order.status === "remis_transporteur" && (
          <div className="bg-[#0077B6]/8 border border-[#0077B6]/20 rounded-xl p-3 mb-4" data-testid="handed-carrier-note">
            <p className="text-sm font-semibold text-[#0077B6] flex items-center gap-2"><Truck className="h-4 w-4" /> {tr("orders.handedToCarrier")}{order.carrier_name ? ` — ${order.carrier_name}` : ""}</p>
            {order.handed_to_carrier_at && <p className="text-xs text-[#5C574F] mt-0.5">{tr("orders.handedOn", { date: fmtDateTime(order.handed_to_carrier_at) })}</p>}
            {order.carrier_batch_id && (
              <a
                data-testid="order-manifest-btn"
                href={`${API}/orders/carrier-batch/${order.carrier_batch_id}/manifest.pdf`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#0077B6] font-semibold hover:underline"
              >
                <FileDown className="h-4 w-4" /> {tr("orders.downloadManifest")}
              </a>
            )}
          </div>
        )}

        {order.shipping_source === "flagship" && order.mode === "livraison" && (
          <div className="bg-white border border-[#1B4332]/20 rounded-xl p-4 mb-4" data-testid="flagship-label-section">
            <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Truck className="h-4 w-4 text-[#1B4332]" /> {tr("orders.flagshipTitle")}{order.carrier ? ` — ${order.carrier}` : ""}</p>
            {!order.flagship_shipment_id ? (
              <>
                <p className="text-xs text-[#9c968a] mb-3">{tr("orders.flagshipHint")}</p>
                <button
                  data-testid="generate-flagship-label-btn"
                  onClick={generateFlagshipLabel}
                  disabled={genLabel}
                  className="w-full h-11 rounded-xl bg-[#1B4332] text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
                >
                  <FileDown className="h-4 w-4" /> {genLabel ? tr("common.sending") : tr("orders.flagshipGenerate")}
                </button>
              </>
            ) : (
              <>
                <p className="text-xs text-[#5C574F] mb-2">{tr("orders.trackingNumber")} : <span className="font-semibold">{order.tracking_number}</span></p>
                <a
                  data-testid="download-flagship-label-btn"
                  href={`${API}/orders/${order.order_id}/flagship-label.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full h-11 rounded-xl bg-[#1B4332] text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                  <FileDown className="h-4 w-4" /> {tr("orders.flagshipDownload")}
                </a>
                <button
                  data-testid="cancel-flagship-label-btn"
                  onClick={cancelFlagshipLabel}
                  disabled={genLabel}
                  className="w-full h-10 mt-2 rounded-xl bg-white border border-[#D00000]/40 text-[#D00000] font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60"
                >
                  {tr("orders.flagshipCancel")}
                </button>
              </>
            )}
          </div>
        )}

        {order.shipping_source !== "flagship" && order.mode === "livraison" && (order.status === "en_livraison" || order.status === "remis_transporteur" || order.tracking_number) && (
          <div className="bg-white border border-[#E5E0D8] rounded-xl p-4 mb-4" data-testid="tracking-section">
            <p className="text-sm font-semibold flex items-center gap-2 mb-1"><Truck className="h-4 w-4 text-[#0077B6]" /> {tr("orders.trackingTitle")}</p>
            <p className="text-xs text-[#9c968a] mb-2">{tr("orders.trackingHint")}</p>
            <div className="flex gap-2">
              <input data-testid="tracking-input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder={tr("orders.trackingPlaceholder")} className="flex-1 h-10 rounded-xl border border-[#E5E0D8] px-3 text-sm" />
              <button data-testid="save-tracking-btn" onClick={saveTracking} disabled={savingTracking || !tracking.trim()} className="h-10 px-4 rounded-xl bg-[#1B4332] text-white font-semibold text-sm active:scale-95 transition-transform disabled:opacity-60">{savingTracking ? tr("common.sending") : tr("common.save")}</button>
            </div>
            {order.tracking_url && (
              <a href={order.tracking_url} target="_blank" rel="noreferrer" data-testid="tracking-link" className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#0077B6] font-semibold hover:underline">
                <Link2 className="h-4 w-4" /> {tr("orders.trackingView")}
              </a>
            )}
          </div>
        )}

        <div className="bg-[#F9F6F0] rounded-xl p-4 space-y-2 mb-4">
          {order.items.map((it, i) => (
            <div key={`${it.product_id}-${it.size || ""}-${i}`} className="flex justify-between text-sm">
              <span className="text-[#5C574F]">{it.name} ×{it.qty}{it.size ? <span className="ml-1.5 text-[11px] font-semibold text-[#1B4332] bg-[#1B4332]/10 px-1.5 py-0.5 rounded" data-testid={`order-item-size-${i}`}>{tr("checkout.size")} {it.size}</span> : null}</span>
              <span className="font-medium">{money(it.unit_price * it.qty, currency)}</span>
            </div>
          ))}
          <div className="border-t border-[#E5E0D8] pt-2 flex justify-between text-sm"><span>{tr("orders.subtotal")}</span><span className="font-semibold">{money(order.subtotal, currency)}</span></div>
          {order.delivery_fee > 0 && <div className="flex justify-between text-sm"><span>{tr("orders.deliveryLine", { zone: order.delivery_quartier })}</span><span className="font-semibold">{money(order.delivery_fee, currency)}</span></div>}
          {(order.tax_lines || []).map((tx) => (
            <div key={tx.name} className="flex justify-between text-sm text-[#5C574F]"><span>{tx.name}</span><span>{money(tx.amount, currency)}</span></div>
          ))}
          <div className="flex justify-between font-display font-bold text-[#E85D04]"><span>{tr("orders.totalClient")}</span><span>{money(order.total, currency)}</span></div>
          {order.service_fee > 0 && <div className="flex justify-between text-xs text-[#9c968a] border-t border-[#E5E0D8] pt-2"><span>{tr("orders.serviceFee")}</span><span>− {money(order.service_fee, currency)}</span></div>}
        </div>

        <div className="flex items-center justify-between gap-2 text-xs mb-4 -mt-1">
          <div className="min-w-0">
            <p className="text-[#9c968a] truncate">{tr("orders.invoiceNo")} <span className="font-semibold text-[#5C574F]">{order.invoice_number || "—"}</span></p>
            {order.payment_reference && <p className="text-[#9c968a] truncate">{tr("orders.refPayment")} <span className="font-semibold text-[#5C574F]">{order.payment_reference}</span></p>}
            {order.payment_method && <p className="text-[#9c968a] truncate">{tr("orders.method")} <span className="font-semibold text-[#5C574F]">{order.payment_method}</span></p>}
          </div>
          <a
            href={`${API}/public/orders/${order.order_id}/invoice.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="modal-view-pdf"
            className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#F9F6F0] text-[#5C574F] font-semibold hover:text-[#E85D04] transition-colors"
          >
            <FileDown className="h-4 w-4" /> {tr("orders.viewPdf")}
          </a>
        </div>

        {order.status !== "a_valider" && (
        <button
          data-testid="resend-invoice-btn"
          onClick={resendInvoice}
          disabled={sendingInvoice}
          className="w-full h-12 mb-2 rounded-2xl bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
        >
          <Phone className="h-4 w-4" /> {sendingInvoice ? tr("common.sending") : tr("orders.resendInvoice")}
        </button>
        )}
        {(order.invoice_sends?.length > 0 || order.invoice_last_sent_at) && (
          <p className="text-xs text-[#9c968a] text-center mb-4" data-testid="invoice-send-history">
            {tr("orders.invoiceSentHist", { times: order.invoice_sends?.length > 1 ? `${order.invoice_sends.length}× ` : "", date: fmtDateTime(order.invoice_last_sent_at || order.invoice_sends[order.invoice_sends.length - 1]) })}
          </p>
        )}

        {order.status === "a_valider" && (
          <div className="space-y-3" data-testid="validate-section">
            <div className="bg-[#F9F6F0] rounded-xl p-3 text-sm" data-testid="validate-pay-mention">
              {order.paid && order.payment_type === "online" ? (
                <span className="font-semibold text-[#2D6A4F]">✅ {tr("orders.mPaidOnline")} — {money(order.total, currency)}</span>
              ) : order.payment_type === "on_delivery" ? (
                <span className="font-semibold text-[#E85D04]">{tr("orders.mCod")} — {money(order.total, currency)}</span>
              ) : order.payment_type === "on_pickup" || order.mode === "retrait" ? (
                <span className="font-semibold text-[#7048E8]">{tr("orders.mPickup")} — {money(order.total, currency)}</span>
              ) : (
                <span className="font-semibold text-[#B07d00]">{tr("orders.awaitingPayment")} — {money(order.total, currency)}</span>
              )}
            </div>
            <button data-testid="validate-order-btn" onClick={validate} disabled={busy} className="w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60">
              {busy ? tr("orders.validating") : tr("orders.validateToPrep")}
            </button>
            {!order.paid && (
              <button data-testid="refuse-order-btn" onClick={() => setShowRefuse(true)} disabled={busy} className="w-full h-12 rounded-2xl bg-white border border-[#D00000]/30 text-[#D00000] font-semibold active:scale-95 transition-transform disabled:opacity-60">
                {tr("orders.refuseOrder")}
              </button>
            )}
          </div>
        )}

        {order.status === "refuse" && order.refusal_reason && (
          <div className="bg-[#D00000]/8 border border-[#D00000]/20 rounded-xl p-3 mb-2" data-testid="refusal-reason">
            <p className="text-sm text-[#D00000] font-semibold">{tr("orders.refusedTitle")}</p>
            <p className="text-sm text-[#5C574F] mt-0.5">{tr("orders.motif", { reason: order.refusal_reason })}</p>
          </div>
        )}

        {order.status === "a_payer" && order.payment_type === "interac_etransfer" && (
          <div className="space-y-3" data-testid="interac-payment-section">
            <div className="bg-[#E85D04]/8 border border-[#E85D04]/25 rounded-xl p-3 text-sm">
              <p className="font-semibold text-[#E85D04] flex items-center gap-2"><Banknote className="h-4 w-4" /> {tr("orders.interacTitle")} — {money(order.total, currency)}</p>
              <p className="text-xs text-[#5C574F] mt-1">{tr("orders.interacWait")}</p>
            </div>
            <button data-testid="confirm-interac-btn" onClick={confirmInterac} disabled={confirmingInterac} className="w-full h-14 rounded-2xl bg-[#2D6A4F] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
              <Banknote className="h-5 w-5" /> {confirmingInterac ? tr("common.sending") : tr("orders.interacMarkPaid")}
            </button>
            <p className="text-xs text-center text-[#9c968a]">{tr("orders.interacCommissionNote")}</p>
          </div>
        )}

        {order.status === "a_payer" && order.payment_type !== "interac_etransfer" && (
          <div className="space-y-3" data-testid="payment-section">
            {order.payment_links?.wave ? (
              <>
                <p className="text-sm font-semibold text-[#5C574F]">{tr("orders.payLinksMM")}</p>
                <div className="grid grid-cols-3 gap-2">
                  <PayBtn href={order.payment_links?.wave} label="Wave" bg="#1DA1F2" />
                  <PayBtn href={order.payment_links?.mtn} label="MTN" bg="#FFCB05" color="#000" />
                  <PayBtn href={order.payment_links?.orange} label="Orange" bg="#FF7900" />
                </div>
              </>
            ) : (
              <p className="text-sm text-[#5C574F] bg-[#F9F6F0] rounded-xl p-3">{tr("orders.cardNote")}</p>
            )}
            <button data-testid="share-payment-btn" onClick={sharePaymentLink} className="w-full h-14 rounded-2xl bg-[#1B4332] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Phone className="h-5 w-5" /> {tr("orders.sendLinkWa")}
            </button>
            <p className="text-xs text-center text-[#9c968a]">{tr("orders.payClientNote")}</p>
          </div>
        )}

        {order.status === "echec_livraison" && order.delivery_failed_reason && (
          <div className="bg-[#D00000]/8 border border-[#D00000]/20 rounded-xl p-3 mb-2" data-testid="delivery-failed-reason">
            <p className="text-sm text-[#D00000] font-semibold">{tr("orders.deliveryFailedTitle")}</p>
            <p className="text-sm text-[#5C574F] mt-0.5">{tr("orders.motif", { reason: order.delivery_failed_reason })}</p>
            <p className="text-xs text-[#9c968a] mt-1">{tr("orders.rescheduleHint")}</p>
          </div>
        )}

        {order.status === "echec_livraison" && (
          <button data-testid="reschedule-btn" onClick={() => { setReNotify(true); setReNote(""); setShowReschedule(true); }} disabled={busy} className="w-full h-12 mb-3 rounded-2xl bg-[#0077B6] text-white font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
            <Truck className="h-5 w-5" /> {tr("orders.reschedule")}
          </button>
        )}

        {showReschedule && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowReschedule(false)} data-testid="reschedule-modal">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-display text-lg font-extrabold mb-1">{tr("orders.reschedule")}</h3>
              <p className="text-sm text-[#5C574F] mb-3">{tr("orders.rescheduleSub")}</p>
              <label className="flex items-center gap-2 bg-[#F9F6F0] rounded-xl p-3 mb-3 cursor-pointer">
                <input type="checkbox" data-testid="reschedule-notify" checked={reNotify} onChange={(e) => setReNotify(e.target.checked)} className="h-4 w-4 accent-[#0077B6]" />
                <span className="text-sm font-medium">{tr("orders.notifyClient")}</span>
              </label>
              {reNotify && (
                <textarea data-testid="reschedule-note" value={reNote} onChange={(e) => setReNote(e.target.value)} placeholder={tr("orders.rescheduleNotePh")} rows={2} className="w-full bg-[#F9F6F0] rounded-xl px-3 py-2 outline-none border border-transparent focus:border-[#0077B6] mb-4 resize-none text-sm" />
              )}
              <div className="flex gap-2">
                <button data-testid="reschedule-cancel" onClick={() => setShowReschedule(false)} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">{tr("common.cancel")}</button>
                <button data-testid="reschedule-confirm" onClick={reschedule} disabled={busy} className="flex-1 h-12 rounded-2xl bg-[#0077B6] text-white font-bold disabled:opacity-60">{busy ? "…" : tr("common.confirm")}</button>
              </div>
            </div>
          </div>
        )}

        {order.status === "preparation" && (
          <button data-testid="mark-shipped-btn" onClick={() => setStatus(order.mode === "retrait" ? "en_attente_retrait" : "en_livraison")} disabled={busy} className="w-full h-12 mt-3 rounded-2xl bg-[#0077B6] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
            {order.mode === "retrait" ? <Store className="h-5 w-5" /> : <Truck className="h-5 w-5" />}
            {tr("orders.finishPrep")}
          </button>
        )}

        {order.status === "en_attente_retrait" && (
          <button data-testid="mark-pickedup-btn" onClick={() => setStatus("livre")} disabled={busy} className="w-full h-12 mt-3 rounded-2xl bg-[#7048E8] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Check className="h-5 w-5" /> {tr("orders.markPickedUp")}
          </button>
        )}

        {(order.status === "en_livraison" || order.status === "remis_transporteur" || order.status === "a_payer" || order.status === "echec_livraison") && (
          order.in_tour ? (
            <div className="w-full mt-3 rounded-2xl bg-[#0077B6]/10 text-[#0077B6] p-3 text-center text-sm font-semibold" data-testid="locked-delivery-note">
              <Truck className="h-4 w-4 inline mr-1" /> {tr("orders.lockedNote")}
            </div>
          ) : (
            <button data-testid="mark-delivered-btn" onClick={() => setStatus("livre")} disabled={busy} className="w-full h-12 mt-3 rounded-2xl bg-[#2D6A4F] text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Check className="h-5 w-5" /> {tr("orders.markDelivered")}
            </button>
          )
        )}
        {order.status !== "annule" && order.status !== "livre" && order.status !== "a_valider" && order.status !== "refuse" && (
          <button data-testid="cancel-order-btn" onClick={() => setStatus("annule")} disabled={busy} className="w-full h-11 mt-2 rounded-2xl border border-[#D00000]/30 text-[#D00000] font-semibold text-sm">
            {tr("orders.cancelOrder")}
          </button>
        )}

        {showRefuse && (
          <div className="absolute inset-0 z-10 bg-black/30 flex items-end sm:items-center justify-center rounded-t-3xl sm:rounded-3xl" onClick={() => setShowRefuse(false)} data-testid="refuse-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-white w-full rounded-t-3xl sm:rounded-2xl p-5 m-0 sm:m-4 shadow-xl">
              <h4 className="font-display text-lg font-bold mb-1">{tr("orders.refuseTitle")}</h4>
              <p className="text-sm text-[#5C574F] mb-3">{tr("orders.refuseSub")}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[["rp_stock", tr("orders.rp_stock")], ["rp_unavailable", tr("orders.rp_unavailable")], ["rp_outzone", tr("orders.rp_outzone")], ["rp_price", tr("orders.rp_price")]].map(([k, r]) => (
                  <button key={k} data-testid={`refuse-preset-${k}`} onClick={() => setRefuseReason(r)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${refuseReason === r ? "bg-[#D00000] text-white border-[#D00000]" : "bg-[#F9F6F0] text-[#5C574F] border-transparent"}`}>{r}</button>
                ))}
              </div>
              <input
                data-testid="refuse-reason-input"
                value={refuseReason}
                onChange={(e) => setRefuseReason(e.target.value)}
                placeholder={tr("orders.refuseReasonPh")}
                className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#D00000] mb-4"
              />
              <div className="flex gap-2">
                <button data-testid="refuse-cancel-btn" onClick={() => setShowRefuse(false)} className="flex-1 h-12 rounded-2xl bg-[#F9F6F0] text-[#5C574F] font-semibold">{tr("common.cancel")}</button>
                <button data-testid="refuse-confirm-btn" onClick={refuse} disabled={busy} className="flex-1 h-12 rounded-2xl bg-[#D00000] text-white font-bold disabled:opacity-60">{busy ? "…" : tr("orders.confirmRefuse")}</button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function PayBtn({ href, label, bg, color = "#fff" }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={`pay-${label.toLowerCase()}`} className="h-12 rounded-xl flex items-center justify-center font-bold text-sm active:scale-95 transition-transform" style={{ background: bg, color }}>
      {label}
    </a>
  );
}
